import type { ThemeStyles, ThemeStyleToken } from './theme-types';
import {
  STYLE_FALLBACKS,
  THEME_DARK_STYLE_DEFAULTS,
  THEME_STYLE_DEFAULTS,
  THEME_STYLE_TOKENS,
} from './theme-constants';

export function resolveThemeToken(styles: ThemeStyles | undefined, key: ThemeStyleToken): string {
  const raw = styles?.[key];
  if (raw !== undefined && raw !== '') return raw;

  const discoveredDefaultValue = THEME_STYLE_DEFAULTS[key];
  if (discoveredDefaultValue !== '') return discoveredDefaultValue;

  const fallbackKey = STYLE_FALLBACKS[key];
  if (fallbackKey) return resolveThemeToken(styles, fallbackKey);

  return '';
}

/**
 * Resolved token map for the live preview. Matches SDK ThemeContext dark merge when `dark` is true:
 * light defaults → dark defaults (if dark) → partner form styles → shell/field fallbacks.
 */
export function mergePreviewThemeStyles(
  formStyles: ThemeStyles | undefined,
  dark: boolean,
): Record<ThemeStyleToken, string> {
  const partnerStyles = formStyles ?? {};
  const base = structuredClone(THEME_STYLE_DEFAULTS);
  if (dark) {
    Object.assign(base, THEME_DARK_STYLE_DEFAULTS);
  }
  for (const key of THEME_STYLE_TOKENS) {
    // Zod `ThemeStyles` is not indexable with `ThemeStyleToken`; `keyof` is.
    const partnerOverride = partnerStyles[key as keyof ThemeStyles];
    if (partnerOverride !== undefined) {
      base[key] = partnerOverride;
    }
  }

  // One hop only (`shell`/`field` → `background`); extend if STYLE_FALLBACKS gains chains.
  for (const [key, fallbackKey] of Object.entries(STYLE_FALLBACKS) as [
    ThemeStyleToken,
    ThemeStyleToken,
  ][]) {
    if (!fallbackKey) continue;
    if (!base[key]?.trim()) {
      base[key] = base[fallbackKey] ?? '';
    }
  }

  return base;
}
